/**
 * Adaptive bottom-nav composition — overflow promotion from the Více menu SSOT.
 *
 * Canonical overflow list: `buildAccountOverflowMenuItems` (accountMenuItems.ts).
 * Composition mode / 4th-slot promotion derives from that list only — no parallel
 * destination builder. Více renders only when overflow has 2+ items; a single
 * item is promoted to the 4th primary slot.
 */
import type { TFunction } from 'i18next';
import {
  buildAccountOverflowMenuItems,
  type AccountMenuItem,
} from '../account/accountMenuItems.js';
import type { GiftSurfaceVisibility } from './giftSurfaceVisibility.js';

export interface NavDestination {
  readonly id: PromotableOverflowId | UtilityOverflowId;
  readonly href: string;
  readonly labelKey: string;
  readonly testId: string;
}

/** Destinations that may occupy the 4th primary slot when alone in overflow. */
export type PromotableOverflowId = 'overview' | 'donate' | 'recurring' | 'my-donations';

/** Utility More rows — primary Objednávky stays on bottom-nav (never overflow-shrunk). */
export type UtilityOverflowId = 'find-order';

/**
 * Reachable modes only: Přehled is always in overflow membership, so length ≥ 1
 * on normal input. Defensive empty → treat as single-promote impossible; callers
 * that somehow pass empty get moreDrawer with empty gift/utility (no Více rows).
 */
export type NavComposition =
  | {
      readonly mode: 'singlePromoted';
      readonly item: NavDestination;
      readonly utilityItems: readonly NavDestination[];
    }
  | {
      readonly mode: 'moreDrawer';
      readonly giftOverflow: readonly NavDestination[];
      readonly utilityItems: readonly NavDestination[];
      /** Full overflow membership (same paths as More sheet). */
      readonly overflowItems: readonly NavDestination[];
    };

export const PROMOTABLE_OVERFLOW_REGISTRY: readonly {
  readonly id: Exclude<PromotableOverflowId, 'overview'>;
  readonly flag: keyof Pick<
    GiftSurfaceVisibility,
    'donateVisible' | 'recurringVisible' | 'myDonationsVisible'
  >;
  readonly labelKey: string;
  readonly testId: string;
  readonly pathSuffix: string;
}[] = [
  {
    id: 'donate',
    flag: 'donateVisible',
    labelKey: 'hub.donate.label',
    testId: 'customer-bottom-nav-donate',
    pathSuffix: '/donate',
  },
  {
    id: 'recurring',
    flag: 'recurringVisible',
    labelKey: 'account.menu.recurring.label',
    testId: 'customer-bottom-nav-recurring',
    pathSuffix: '/recurring-donations',
  },
  {
    id: 'my-donations',
    flag: 'myDonationsVisible',
    labelKey: 'account.menu.donations.label',
    testId: 'customer-bottom-nav-my-donations',
    pathSuffix: '/donations',
  },
];

export interface BuildCustomerNavCompositionInput {
  readonly tenantCode: string;
  readonly t: TFunction<'customer'>;
  readonly gift: GiftSurfaceVisibility;
  readonly isAuthenticated: boolean;
  /** Optional pickup UX — Najít objednávku; omit when infra Off. */
  readonly showFindOrder?: boolean;
  /** Kupóny / account-home overview — `VITE_CUSTOMER_KUPONY_ENABLED`. */
  readonly showKupony?: boolean;
  /** Obchody `/shops` — `VITE_CUSTOMER_SHOPS_NAV_ENABLED`. */
  readonly showShops?: boolean;
}

function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

/** Map account overflow `to` → nav destination id + testId (composition + bar). */
export function resolveOverflowNavDestination(
  item: AccountMenuItem,
): NavDestination | null {
  const to = normalizePath(item.to);
  if (/\/account\/donate$/.test(to)) {
    return {
      id: 'donate',
      href: item.to,
      labelKey: 'hub.donate.label',
      testId: 'customer-bottom-nav-donate',
    };
  }
  if (/\/account\/recurring-donations$/.test(to)) {
    return {
      id: 'recurring',
      href: item.to,
      labelKey: 'account.menu.recurring.label',
      testId: 'customer-bottom-nav-recurring',
    };
  }
  if (/\/account\/donations$/.test(to)) {
    return {
      id: 'my-donations',
      href: item.to,
      labelKey: 'account.menu.donations.label',
      testId: 'customer-bottom-nav-my-donations',
    };
  }
  if (/\/orders\/find$/.test(to)) {
    return {
      id: 'find-order',
      href: item.to,
      labelKey: 'hub.orders.label',
      testId: 'customer-more-find-order',
    };
  }
  if (/\/account$/.test(to)) {
    return {
      id: 'overview',
      href: item.to,
      labelKey: 'account.menu.home.label',
      testId: 'customer-bottom-nav-overview',
    };
  }
  return null;
}

/**
 * Canonical overflow destinations — same membership as More sheet rows
 * (`buildAccountOverflowMenuItems`).
 */
export function buildOverflowNavDestinations(
  input: BuildCustomerNavCompositionInput,
): NavDestination[] {
  const donationVisibility = {
    donate: input.gift.donateVisible,
    recurring: input.gift.recurringVisible,
    myDonations: input.gift.myDonationsVisible,
  };
  const menuItems = buildAccountOverflowMenuItems(
    input.tenantCode,
    input.t,
    donationVisibility,
    {
      showFindOrder: input.showFindOrder === true,
      showKupony: input.showKupony === true,
      showShops: input.showShops === true,
    },
  );
  const destinations: NavDestination[] = [];
  for (const item of menuItems) {
    const dest = resolveOverflowNavDestination(item);
    if (dest !== null) {
      destinations.push(dest);
    }
  }
  return destinations;
}

export function buildCustomerNavComposition(
  input: BuildCustomerNavCompositionInput,
): NavComposition {
  void input.isAuthenticated;
  const overflowItems = buildOverflowNavDestinations(input);
  const giftOverflow = overflowItems.filter(
    (item) =>
      item.id === 'donate' || item.id === 'recurring' || item.id === 'my-donations',
  );
  const utilityItems = overflowItems.filter((item) => item.id === 'find-order');

  if (overflowItems.length === 1) {
    return {
      mode: 'singlePromoted',
      item: overflowItems[0] as NavDestination,
      utilityItems,
    };
  }

  return {
    mode: 'moreDrawer',
    giftOverflow,
    utilityItems,
    overflowItems,
  };
}
